/**
 * KAN-964: календарь месяца раздела "Встречи и перезвоны": в днях число шагов
 * (GET today-steps/month/), выбранный день подсвечен, сегодня обведён.
 * Сетка и стрелки общие (components/calendar/MonthCalendar).
 */
import { MonthCalendar } from '@/components/calendar/MonthCalendar'
import { useTodayStepsMonth } from '@/hooks/useTodaySteps'
import { formatDayFull, type MonthRef } from '@/lib/crmRange'
import { monthKey } from '@/lib/todaySteps'

interface TodayStepsCalendarProps {
  month: MonthRef
  onMonthChange: (month: MonthRef) => void
  selected: string
  today: string
  onPick: (day: string) => void
}

function dayClass(selected: boolean, isToday: boolean): string {
  const base = 'flex h-9 w-9 flex-col items-center justify-center rounded-md text-sm leading-none tabular-nums'
  if (selected) return `${base} bg-blue-600 text-white`
  if (isToday) return `${base} font-semibold text-blue-700 ring-1 ring-inset ring-blue-600 hover:bg-blue-50`
  return `${base} text-gray-800 hover:bg-gray-100`
}

export function TodayStepsCalendar({ month, onMonthChange, selected, today, onPick }: TodayStepsCalendarProps) {
  const { data } = useTodayStepsMonth(monthKey(month))
  const days = data?.days ?? {}

  return (
    <MonthCalendar
      month={month}
      onMonthChange={onMonthChange}
      testId="today-steps-calendar"
      monthTestId="today-steps-month"
      weekdayClassName="h-6 w-9"
      renderDay={(day) => {
        const count = days[day] ?? 0
        const isSelected = day === selected
        return (
          <button
            type="button"
            data-testid={`today-steps-day-${day}`}
            aria-label={count ? `${formatDayFull(day)}, шагов: ${count}` : formatDayFull(day)}
            aria-pressed={isSelected}
            aria-current={day === today ? 'date' : undefined}
            className={dayClass(isSelected, day === today)}
            onClick={() => onPick(day)}
          >
            <span>{Number(day.slice(8))}</span>
            {count > 0 && (
              <span
                data-testid={`today-steps-day-count-${day}`}
                className={`mt-0.5 text-[10px] font-semibold ${isSelected ? 'text-blue-100' : 'text-blue-600'}`}
              >
                {count}
              </span>
            )}
          </button>
        )
      }}
    />
  )
}
